import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { LtiTools } from "../../../../interfaces";
import { UsersManagementProps } from "../../../../pages/v2/UsersPage";
import { DataGrid, GridColDef, GridColumns } from "@mui/x-data-grid";
import { ThemeContext } from "../../../../context/ThemeContext";
import { ThemeCtx } from "../../../../interfaces";
import { SettingsButton } from "../utils/SettingsButton";
import { ViewLtiTool } from "../ViewDetails/ViewLtiTool";
import Typography from "@mui/material/Typography";
import { PopUpDialog } from "../../utils/PopUpDialog";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import axios from "../../../../services/axios";
import { ErrorToast, SuccessToast } from "../../utils/Toast";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
  },
  dataTable: {
    borderRadius: "8px",
    backgroundColor: "white",
    borderColor: "#E8E8E8",
    "& .MuiDataGrid-iconButtonContainer": {
      marginLeft: "auto",
    },
    "& .MuiDataGrid-columnHeaders": {
      height: "50px",
      minHeight: "unset",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
  },
  dataTableHeader: {
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  usersTable: {
    "& thead": {
      color: "#FFF",
      backgroundColor: theme.palette.primary.main,
    },
    "& th": {
      padding: "0.625rem",
    },
    "& td": {
      padding: "0.625rem",
    },
  },
  evenRow: {
    backgroundColor: "#FFF",
  },
  oddRow: {
    backgroundColor: "#F5F5F5",
  },
  deleteUserButton: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "2.25rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

export const LtiToolsTable: React.FunctionComponent<UsersManagementProps> = (props) => {
  const classes = useStyles();

  const themeCtx = useContext(ThemeContext) as ThemeCtx;
  const [selectedLtiTool, setSelectedLtiTool] = useState<LtiTools | null>(null);
  const [assignLtiToCoursePromptOpen, setAssignLtiToCoursePromptOpen] =
    useState<boolean>(false);
  const [viewLtiToolModalOpen, setViewLtiToolModalOpen] = useState<boolean>(false);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const handleSelectTool = (obj: any) => {
    const tool = props.tools?.find((item) => item.id === obj.row.id);
    if (tool) {
      setSelectedLtiTool(tool);
      setViewLtiToolModalOpen(true);
    }
  };

  useEffect(() => {
    const getCourseList = async () => {
      try {
        let response = await axios.get(`/courses`);
        setAllCourses(response.data);
      } catch (e) {
        console.log(e);
      }
    };
    console.log(":viewLtiToolModalOpen", assignLtiToCoursePromptOpen);
    if (assignLtiToCoursePromptOpen) {
      getCourseList();
    }
  }, [assignLtiToCoursePromptOpen]);

  const renderCell = (params: any) => {
    return (
      <div
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {params.value}
      </div>
    );
  };

  const renderCellDate = (params: any) => {
    return (
      <div
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {params.value
          ? new Date(params.value).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          : ""}
      </div>
    );
  };

  const assignLtiToolToCourse = async () => {
    try {
      let response = axios.put(`/course/${selectedCourseId}`, {
        lti_tool_client_id: selectedLtiTool?.clientId,
      });
      console.log("response", response);
      SuccessToast("Lti Tool assigned to course successfully");
      setAssignLtiToCoursePromptOpen(false);
    } catch (e) {
      console.log(e);
      ErrorToast("Error while assigning Lti Tool to course");
      setAssignLtiToCoursePromptOpen(false);
    }
  };

  const fieldMap = new Map<string, GridColDef<LtiTools>>([
    [
      "toolName",
      {
        field: "toolName",
        headerName: "Tool Name",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.toolName}
            </div>
          );
        },
      },
    ],
    [
      "toolDescription",
      {
        field: "toolDescription",
        headerName: "Tool Description",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.toolDescription}
            </div>
          );
        },
      },
    ],
    [
      "clientId",
      {
        field: "clientId",
        headerName: "Client Id",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.clientId}
            </div>
          );
        },
      },
    ],
    [
      "clientSecret",
      {
        field: "clientSecret",
        headerName: "Client Secret",
        flex: 3,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.clientSecret}
            </div>
          );
        },
      },
    ],
    [
      "deploymentId",
      {
        field: "deploymentId",
        headerName: "Deployment Id",
        flex: 3,
        headerClassName: classes.dataTableHeader,
        renderCell,
      },
    ],
    [
      "ownerEmail",
      {
        field: "ownerEmail",
        headerName: "Owner Email",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.toolMetadata?.owner_email}
            </div>
          );
        },
      },
    ],
    [
      "createdAt",
      {
        field: "createdAt",
        headerName: "Created At",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: renderCellDate,
      },
    ],
    [
      "updatedAt",
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: renderCellDate,
      },
    ],
    [
      "action",
      {
        field: "action",
        headerName: "Actions",
        headerAlign: "right",
        headerClassName: classes.dataTableHeader,
        align: "right",
        minWidth: 55,
        sortable: false,
        renderCell: (params) => {
          const actions = [
            // {
            //   label: "Edit",
            //   color: "#747474",
            //   onClick: () => null,
            //   visible: true,
            //   isDisabled: false
            // },
            {
              label: "View",
              color: "#747474",
              onClick: () => handleSelectTool(params),
              visible: true,
              isDisabled: false,
            },
            {
              label: "Use for course",
              color: "#747474",
              onClick: () => {
                setSelectedLtiTool(params.row);
                setAssignLtiToCoursePromptOpen(true);
              },
              isDisabled: false,
              visible: true,
            },
            // {
            //   label: "Delete",
            //   color: "#CC2F2F",
            //   onClick: () => null,
            //   visible: true,
            //   isDisabled: false,
            // },
          ];
          return <SettingsButton sectionName={params.row.toolName} actions={actions} />;
        },
      },
    ],
  ]);

  const getField = (fieldName: string): GridColDef<LtiTools> | void => {
    const field = fieldMap.get(fieldName);
    return field;
  };

  const buildColumnDef = (): GridColumns<LtiTools> => {
    const columns: GridColumns<LtiTools> = [];

    if (!props.columnNames) {
      columns.push(
        ...[
          getField("toolName")!,
          //getField("toolDescription")!,
          getField("clientId")!,
          //getField("clientSecret")!,
          //getField("deploymentId")!,
          getField("ownerEmail")!,
          getField("createdAt")!,
          //getField("updatedAt")!,
          getField("action")!,
        ]
      );
    } else {
      props.columnNames.forEach((fieldName) => {
        columns.push(getField(fieldName)!);
      });
    }

    return columns;
  };

  return (
    <>
      <div className={classes.root}>
        <div style={{ width: "100%" }}>
          <DataGrid
            className={classes.dataTable}
            rows={props.tools || []}
            columns={buildColumnDef()}
            sortingMode="server"
            onSortModelChange={props.sort?.handleSort}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: props.sort?.field || "first_name",
                    sort: props.sort?.sort || "asc",
                  },
                ],
              },
            }}
            autoHeight
            disableSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooter
          />
        </div>
      </div>
      <ViewLtiTool
        open={viewLtiToolModalOpen}
        onClose={() => {
          setSelectedLtiTool(null);
          setViewLtiToolModalOpen(false);
        }}
        onEdit={() => {}}
        currentUser={props.currentUser}
        tool={selectedLtiTool}
      />
      <PopUpDialog
        ctaFooterVariant={"stretched"}
        promptOpen={assignLtiToCoursePromptOpen}
        handleClose={() => {
          setAssignLtiToCoursePromptOpen(false);
        }}
        handleOkClick={assignLtiToolToCourse}
        handleCancelClick={() => {
          setAssignLtiToCoursePromptOpen(false);
        }}
        okButtonLabel={"Use"}
        popupTitle={`Use ${selectedLtiTool?.toolName} Lti Tool for Course`}
        cancelButtonLabel={"Cancel"}>
        <Typography variant="body1" sx={{ marginBottom: "16px" }}>
          Please Select the Course to assign LTI tool to{" "}
        </Typography>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="course-list-label">Courses</InputLabel>
          <Select
            labelId="course-list-label"
            id="course-list"
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value as string)}
            label="Course list">
            {allCourses.map((course: any) => {
              return <MenuItem value={course.id}>{course.name}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </PopUpDialog>
    </>
  );
};
